import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Link, useNavigate } from "react-router-dom";
import AdminNavbar from "../Layout/AdminNavbar";
import API_BASE_URL from "../config";

const T = {
  primary: "#6366f1",
  primaryLight: "#eef2ff",
  primaryDark: "#4f46e5",
  surface: "#ffffff",
  surfaceAlt: "#f8fafc",
  border: "#e2e8f0",
  borderLight: "#f1f5f9",
  text: "#0f172a",
  textMuted: "#64748b",
  textLight: "#94a3b8",
  success: "#10b981",
  successBg: "#ecfdf5",
  danger: "#ef4444",
  dangerBg: "#fef2f2",
  warning: "#f59e0b",
  warningBg: "#fffbeb",
  radius: "10px",
  radiusSm: "6px",
  shadow: "0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd: "0 4px 12px rgba(99,102,241,0.10)",
};

const badge = (status) => {
  if (!status) return null;
  const map = {
    Active: { bg: T.successBg, color: T.success, dot: T.success },
    Inactive: { bg: T.dangerBg, color: T.danger, dot: T.danger },
    "On Leave": { bg: T.warningBg, color: T.warning, dot: T.warning },
  };
  const style = map[status] || { bg: "#f1f5f9", color: T.textMuted, dot: T.textMuted };
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        padding: "3px 10px",
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 600,
        background: style.bg,
        color: style.color,
        letterSpacing: 0.3,
        whiteSpace: "nowrap",
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: "50%",
          background: style.dot,
          flexShrink: 0,
        }}
      />
      {status}
    </span>
  );
};

const Avatar = ({ emp }) => {
  const initials = [emp.firstName, emp.lastName]
    .filter(Boolean)
    .map((s) => s[0].toUpperCase())
    .join("");
  const colors = [
    "#6366f1","#8b5cf6","#ec4899","#06b6d4","#10b981","#f59e0b","#ef4444","#3b82f6",
  ];
  const colorIdx = initials.charCodeAt(0) % colors.length;

  if (emp.photoPath) {
    return (
      <img
        src={`${API_BASE_URL}/uploads/${emp.photoPath}`}
        alt={initials}
        style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", border: `2px solid ${T.border}` }}
      />
    );
  }
  return (
    <div
      style={{
        width: 34,
        height: 34,
        borderRadius: "50%",
        background: colors[colorIdx],
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: 0.5,
        flexShrink: 0,
      }}
    >
      {initials || "?"}
    </div>
  );
};

const Th = ({ label, sortKey, sortConfig, onSort }) => (
  <th
    onClick={() => onSort(sortKey)}
    style={{
      position: "sticky",
      top: 0,
      background: "#f1f5f9",
      color: T.textMuted,
      fontWeight: 600,
      fontSize: 11,
      textTransform: "uppercase",
      letterSpacing: 0.6,
      padding: "10px 12px",
      whiteSpace: "nowrap",
      cursor: "pointer",
      userSelect: "none",
      borderBottom: `2px solid ${T.border}`,
      zIndex: 10,
    }}
  >
    {label}
    {sortKey && (
      <span style={{ marginLeft: 4, opacity: sortConfig.key === sortKey ? 1 : 0.3 }}>
        {sortConfig.key === sortKey ? (sortConfig.direction === "asc" ? " ↑" : " ↓") : " ↕"}
      </span>
    )}
  </th>
);

const TdPlain = ({ children, style }) => (
  <td style={{ padding: "10px 12px", fontSize: 13, color: T.text, whiteSpace: "nowrap", ...style }}>
    {children}
  </td>
);

const fmt = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleDateString("en-GB") : <span style={{ color: T.textLight }}>—</span>;

const val = (v) => v || <span style={{ color: T.textLight }}>—</span>;

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [departmentFilter, setDepartmentFilter] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [downloading, setDownloading] = useState(false);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    const roles = JSON.parse(localStorage.getItem("roles") || "[]");
    if (!token) { toast.error("You must be logged in."); navigate("/"); return; }
    if (!roles.includes("ROLE_ADMIN")) { toast.error("Not authorized."); navigate("/"); return; }
  }, [navigate]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    fetch(`${API_BASE_URL}/api/employees/list`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((res) => { if (!res.ok) throw new Error("Failed to fetch employees"); return res.json(); })
      .then((data) => { setEmployees(data); setFilteredEmployees(data); })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let result = [...employees];
    if (search) {
      result = result.filter(
        (emp) =>
          emp.firstName?.toLowerCase().includes(search.toLowerCase()) ||
          emp.lastName?.toLowerCase().includes(search.toLowerCase()) ||
          emp.email?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (departmentFilter) {
      result = result.filter(
        (emp) => emp.department?.toLowerCase() === departmentFilter.toLowerCase()
      );
    }
    if (!showInactive) result = result.filter((emp) => emp.user?.status !== "Inactive");
    if (sortConfig.key) {
      result.sort((a, b) => {
        const valA = getNestedValue(a, sortConfig.key);
        const valB = getNestedValue(b, sortConfig.key);
        if (valA == null) return 1;
        if (valB == null) return -1;
        if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
        if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    setFilteredEmployees(result);
  }, [search, departmentFilter, showInactive, employees, sortConfig]);

  const getNestedValue = (obj, key) => key.split(".").reduce((acc, k) => (acc ? acc[k] : null), obj);

  const handleSort = (key) => {
    if (!key) return;
    setSortConfig((prev) =>
      prev.key === key
        ? { key, direction: prev.direction === "asc" ? "desc" : "asc" }
        : { key, direction: "asc" }
    );
  };

  const downloadReport = async (format = "xlsx") => {
    try {
      setDownloading(true);
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_BASE_URL}/api/reports/employees?format=${format}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(await res.text().catch(() => `Failed to download ${format}`));
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition") || "";
      let filename = `Employee_Report.${format === "pdf" ? "pdf" : "xlsx"}`;
      const match = /filename="?([^";]+)"?/.exec(cd);
      if (match?.[1]) filename = match[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (err) {
      toast.error(err.message || "Failed to download");
    } finally {
      setDownloading(false);
    }
  };

  const activeCount = employees.filter((e) => e.user?.status === "Active").length;
  const inactiveCount = employees.filter((e) => e.user?.status === "Inactive").length;

  const departments = [...new Set(employees.map((e) => e.department).filter(Boolean))].sort();

  return (
    <>
      <div className="fixed-top">
        <AdminNavbar />
      </div>

      <div style={{ paddingTop: 100, minHeight: "100vh", background: "#f8fafc", fontFamily: "'Inter', system-ui, sans-serif" }}>
        {loading && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 999,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(248,250,252,0.85)",
              backdropFilter: "blur(2px)",
            }}
          >
            <style>{`
              @keyframes emp-spin { to { transform: rotate(360deg); } }
            `}</style>
            <div
              style={{
                width: 44,
                height: 44,
                border: `3px solid ${T.primaryLight}`,
                borderTopColor: T.primary,
                borderRadius: "50%",
                animation: "emp-spin 0.7s linear infinite",
              }}
            />
            <span style={{ marginTop: 14, fontSize: 13, color: T.textMuted, fontWeight: 500 }}>
              Loading employees…
            </span>
          </div>
        )}
        <div style={{ padding: "20px 24px" }}>

          {/* ── Header ── */}
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              padding: "20px 24px",
              marginBottom: 20,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              boxShadow: T.shadow,
            }}
          >
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 8,
                    background: T.primaryLight,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <i className="bi bi-people-fill" style={{ color: T.primary, fontSize: 17 }} />
                </div>
                <h5 style={{ margin: 0, color: T.text, fontWeight: 700, fontSize: 18 }}>Employees</h5>
              </div>
              <div style={{ display: "flex", gap: 16, marginTop: 4, paddingLeft: 2 }}>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.text }}>{employees.length}</span>Total
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.success }}>{activeCount}</span>Active
                </span>
                <span style={{ color: T.textMuted, fontSize: 12 }}>
                  <span style={{ fontWeight: 700, fontSize: 15, marginRight: 4, color: T.danger }}>{inactiveCount}</span>Inactive
                </span>
              </div>
            </div>
            <button
              onClick={() => navigate("/addemployee")}
              style={{
                background: T.primary,
                color: "#fff",
                border: "none",
                borderRadius: T.radiusSm,
                padding: "8px 16px",
                fontWeight: 600,
                fontSize: 13,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 2px 8px rgba(99,102,241,0.25)",
              }}
            >
              <i className="bi bi-person-plus-fill" /> New Employee
            </button>
          </div>

          {/* ── Toolbar ── */}
          <div
            style={{
              background: T.surface,
              border: `1px solid ${T.border}`,
              borderRadius: T.radius,
              padding: "12px 16px",
              marginBottom: 16,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 10,
              boxShadow: T.shadow,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              {/* Search */}
              <div style={{ position: "relative" }}>
                <i
                  className="bi bi-search"
                  style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: T.textLight, fontSize: 13 }}
                />
                <input
                  type="text"
                  placeholder="Search name or email…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    paddingLeft: 30,
                    paddingRight: 10,
                    paddingTop: 7,
                    paddingBottom: 7,
                    border: `1.5px solid ${search ? T.primary : T.border}`,
                    borderRadius: T.radiusSm,
                    fontSize: 13,
                    outline: "none",
                    width: 200,
                    color: T.text,
                    transition: "border-color 0.15s",
                  }}
                />
              </div>

              {/* Department */}
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                style={{
                  padding: "7px 10px",
                  border: `1.5px solid ${departmentFilter ? T.primary : T.border}`,
                  borderRadius: T.radiusSm,
                  fontSize: 13,
                  color: departmentFilter ? T.text : T.textMuted,
                  outline: "none",
                  cursor: "pointer",
                  background: T.surface,
                }}
              >
                <option value="">All Departments</option>
                {departments.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>

              {/* Show Inactive toggle */}
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 7,
                  cursor: "pointer",
                  fontSize: 13,
                  color: T.textMuted,
                  userSelect: "none",
                }}
              >
                <div
                  onClick={() => setShowInactive(!showInactive)}
                  style={{
                    width: 36,
                    height: 20,
                    borderRadius: 10,
                    background: showInactive ? T.primary : T.border,
                    position: "relative",
                    cursor: "pointer",
                    transition: "background 0.2s",
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      width: 14,
                      height: 14,
                      borderRadius: "50%",
                      background: "#fff",
                      top: 3,
                      left: showInactive ? 19 : 3,
                      transition: "left 0.2s",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                    }}
                  />
                </div>
                Show Inactive
              </label>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {/* Download Excel */}
              <button
                onClick={() => downloadReport("xlsx")}
                disabled={downloading}
                title="Download Excel report"
                style={{
                  padding: "7px 12px",
                  border: `1.5px solid ${T.border}`,
                  borderRadius: T.radiusSm,
                  background: T.surface,
                  color: T.textMuted,
                  fontSize: 13,
                  cursor: downloading ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  fontWeight: 500,
                  opacity: downloading ? 0.6 : 1,
                }}
              >
                <i className="bi bi-file-earmark-excel" style={{ color: "#16a34a" }} />
                {downloading ? "Downloading…" : "Export"}
              </button>

              {/* Count */}
              <div
                style={{
                  padding: "6px 12px",
                  background: T.primaryLight,
                  color: T.primary,
                  borderRadius: T.radiusSm,
                  fontSize: 12,
                  fontWeight: 600,
                }}
              >
                {filteredEmployees.length} result{filteredEmployees.length !== 1 ? "s" : ""}
              </div>
            </div>
          </div>

          {/* ── Table ── */}
          <div
            style={{
              background: T.surface,
              borderRadius: T.radius,
              border: `1px solid ${T.border}`,
              boxShadow: T.shadow,
              overflow: "hidden",
            }}
          >
            <div
              style={{
                maxHeight: "65vh",
                overflowY: "auto",
                overflowX: "auto",
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 transparent",
              }}
            >
              <style>{`
                .emp-table-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
                .emp-table-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
                .emp-table-scroll::-webkit-scrollbar-track { background: transparent; }
              `}</style>
              <table
                className="emp-table-scroll"
                style={{
                  width: "max-content",
                  minWidth: "100%",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                }}
              >
                <thead>
                  <tr>
                    <th style={{ position: "sticky", top: 0, left: 0, zIndex: 20, background: "#f1f5f9", padding: "10px 14px", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.6, color: T.textMuted, borderBottom: `2px solid ${T.border}`, whiteSpace: "nowrap" }}>
                      View | Edit
                    </th>
                    <Th label="Photo" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Employee ID" sortKey="employeeId" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Status" sortKey="user.status" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Name" sortKey="firstName" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Phone" sortKey="phone" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Work Email" sortKey="email" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Department" sortKey="department" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Designation" sortKey="designation" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Birth Date" sortKey="dob" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Joining Date" sortKey="doj" sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Leaving Date" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Marital Status" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Anniversary" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Gender" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Degree" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Institution" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Field of Study" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Edu Start" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Edu End" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Grade" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Permanent Address" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                    <Th label="Current Address" sortKey={null} sortConfig={sortConfig} onSort={handleSort} />
                  </tr>
                </thead>
                <tbody>
                  {filteredEmployees.length > 0 ? (
                    filteredEmployees.map((emp) => (
                      <tr
                        key={emp.id}
                        onMouseEnter={() => setHoveredRow(emp.id)}
                        onMouseLeave={() => setHoveredRow(null)}
                        style={{
                          background: hoveredRow === emp.id ? "#f8faff" : T.surface,
                          transition: "background 0.12s",
                          borderBottom: `1px solid ${T.borderLight}`,
                        }}
                      >
                        {/* Actions — sticky */}
                        <td
                          style={{
                            position: "sticky",
                            left: 0,
                            background: hoveredRow === emp.id ? "#f8faff" : T.surface,
                            padding: "10px 14px",
                            zIndex: 5,
                            borderRight: `1px solid ${T.borderLight}`,
                            whiteSpace: "nowrap",
                          }}
                        >
                          <Link
                            to={`/viewemployee/${emp.id}`}
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: T.primary,
                              textDecoration: "none",
                              padding: "3px 8px",
                              borderRadius: 4,
                              background: T.primaryLight,
                              marginRight: 6,
                            }}
                          >
                            View
                          </Link>
                          <Link
                            to={`/editemployee/${emp.id}`}
                            style={{
                              fontSize: 12,
                              fontWeight: 600,
                              color: "#64748b",
                              textDecoration: "none",
                              padding: "3px 8px",
                              borderRadius: 4,
                              background: "#f1f5f9",
                            }}
                          >
                            Edit
                          </Link>
                        </td>

                        {/* Avatar */}
                        <td style={{ padding: "8px 12px" }}>
                          <Avatar emp={emp} />
                        </td>

                        <TdPlain>
                          <span style={{ fontWeight: 600, color: T.primary, fontFamily: "monospace", fontSize: 12 }}>
                            {val(emp.employeeId)}
                          </span>
                        </TdPlain>
                        <TdPlain>{badge(emp.user?.status)}</TdPlain>
                        <TdPlain style={{ fontWeight: 600 }}>
                          {emp.firstName} {emp.lastName}
                        </TdPlain>
                        <TdPlain>{val(emp.phone)}</TdPlain>
                        <TdPlain style={{ color: T.primary }}>{val(emp.email)}</TdPlain>
                        <TdPlain>
                          {emp.department ? (
                            <span style={{ background: "#ede9fe", color: "#7c3aed", padding: "2px 8px", borderRadius: 4, fontSize: 11, fontWeight: 600 }}>
                              {emp.department}
                            </span>
                          ) : val(null)}
                        </TdPlain>
                        <TdPlain>{val(emp.designation)}</TdPlain>
                        <TdPlain>{fmt(emp.dob)}</TdPlain>
                        <TdPlain>{fmt(emp.doj)}</TdPlain>
                        <TdPlain>{fmt(emp.dol)}</TdPlain>
                        <TdPlain>{val(emp.mstatus)}</TdPlain>
                        <TdPlain>{fmt(emp.doa)}</TdPlain>
                        <TdPlain>{val(emp.gender)}</TdPlain>
                        <TdPlain>{val(emp.degree)}</TdPlain>
                        <TdPlain>{val(emp.institution)}</TdPlain>
                        <TdPlain>{val(emp.fieldOfStudy)}</TdPlain>
                        <TdPlain>{fmt(emp.eduStartDate)}</TdPlain>
                        <TdPlain>{fmt(emp.eduEndDate)}</TdPlain>
                        <TdPlain>{val(emp.grade)}</TdPlain>
                        <TdPlain>
                          {[emp.street1, emp.street2, emp.city, emp.state, emp.postalCode, emp.country]
                            .filter(Boolean)
                            .join(", ") || <span style={{ color: T.textLight }}>—</span>}
                        </TdPlain>
                        <TdPlain>
                          {[emp.currStreet1, emp.currStreet2, emp.currCity, emp.currState, emp.currPostalCode, emp.currCountry]
                            .filter(Boolean)
                            .join(", ") || <span style={{ color: T.textLight }}>—</span>}
                        </TdPlain>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={23} style={{ textAlign: "center", padding: "48px 20px", color: T.textLight }}>
                        <i className="bi bi-people" style={{ fontSize: 32, display: "block", marginBottom: 8 }} />
                        No employees found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {error && (
            <div style={{ marginTop: 12, padding: "10px 14px", background: T.dangerBg, color: T.danger, borderRadius: T.radiusSm, fontSize: 13 }}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default EmployeeList;
